import { faMemo } from '@fortawesome/pro-regular-svg-icons';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '#src/__generated__/globalTypes.ts';
import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { Shards } from '#src/atoms/icons/Shards.tsx';
import { Centered } from '#src/atoms/layout/Centered/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BrandL, BrandS } from '#src/atoms/typography/index.tsx';
import { MY_SORARE_TRANSACTIONS_ESSENCE } from '#src/constants/__generated__/routes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useHasLegacyEssence } from '#src/hooks/commonGame/useHasLegacyEssence.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { usePinnedShardRarity } from '#src/hooks/config/usePinnedShardRarity.ts';
import { getRarityShardColor } from '#src/lib/colors.ts';

import { ResponsiveRaritiesWrapper } from '../ResponsiveRaritiesWrapper.tsx';
import { ShardRarityItem } from './ShardRarityItem.tsx';
import { useFilteredShardsChests } from './useFilteredShardsChests.ts';

const SelectedCurrencyWrapper = styled(Vertical).attrs({
  gap: 2,
})`
  overflow: hidden;
  position: relative;
  padding: var(--double-unit) var(--unit) var(--unit) var(--unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--intermediate-unit);
`;

const CenteredTop = styled(Centered)`
  align-items: start;
`;

type Props = {
  onClick?: () => void;
};

export const ShardDropdownDetails = ({ onClick }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { pinnedShardRarity, pinnedShardCardSet } = usePinnedShardRarity();
  const filteredShardsChests = useFilteredShardsChests();
  const seriesNav = useSeriesNav();
  const hasLegacyEssence = useHasLegacyEssence();

  const { cardShardsChests } = currentUser || {};

  const shardsAmount = pinnedShardRarity
    ? cardShardsChests?.find(
        ({ rarity, cardSet }) =>
          rarity === pinnedShardRarity && cardSet?.slug === pinnedShardCardSet
      )?.cardShardsCount || 0
    : cardShardsChests
        ?.filter(({ rarity }) => rarity !== Rarity.common)
        .reduce((sum, { cardShardsCount }) => sum + cardShardsCount, 0) || 0;

  return (
    <SelectedCurrencyWrapper>
      <CenteredTop
        right={
          <IconButton
            size="small"
            color="quaternary"
            to={MY_SORARE_TRANSACTIONS_ESSENCE}
            icon={faMemo}
          />
        }
      >
        <Vertical gap={0.5} center>
          <Shards
            size={2}
            color={getRarityShardColor(pinnedShardRarity, pinnedShardCardSet)}
          />
          <BrandS
            color={getRarityShardColor(pinnedShardRarity, pinnedShardCardSet)}
          >
            <FormattedMessage
              id="useUserCurrencies.essence"
              defaultMessage="Essence"
            />
          </BrandS>
          <BrandL>
            <FormattedNumber value={shardsAmount || 0} />
          </BrandL>
        </Vertical>
      </CenteredTop>
      {!!(!seriesNav || hasLegacyEssence) && (
        <ResponsiveRaritiesWrapper>
          {filteredShardsChests?.map(({ cardShardsCount, rarity, cardSet }) => {
            return (
              <ShardRarityItem
                key={`${rarity}${cardSet ? `:${cardSet.slug}` : ''}`}
                rarity={rarity}
                cardShardsCount={cardShardsCount}
                cardSet={cardSet}
                onClick={onClick}
              />
            );
          })}
        </ResponsiveRaritiesWrapper>
      )}
    </SelectedCurrencyWrapper>
  );
};
